/**
 * Хук для управления уведомлениями с интеграцией WebSocket
 * Обеспечивает реал-тайм обновления уведомлений
 */

import { useEffect, useCallback, useState } from 'react'
import { useNotificationsStore } from '../store/notificationsStore'
import { useAuthStore } from '../auth/store'
import { useGlobalWebSocket } from '../ws/useGlobalWebSocket'
import { UserNotification } from '../api/notificationsApi'
import { logger } from '../utils/logger'

interface WebSocketNotificationEvent {
  type: 'notification' | 'notifications_marked_read' | 'all_notifications_marked_read' | 'notification_deleted'
  data: any
}

/**
 * Основной хук для работы с уведомлениями
 */
export const useNotifications = () => {
  const {
    notifications,
    unreadCount,
    totalCount,
    isLoading,
    error,
    loadNotifications,
    refreshStats,
    markAsRead,
    markAllAsRead,
    deleteNotificationById,
    deleteAllNotifications,
    deleteNotificationsByOrder,
    addNotification,
    updateNotificationStatus,
    removeNotification,
    clearError
  } = useNotificationsStore()
  
  // WebSocket для реал-тайм обновлений
  const { isConnected, service } = useGlobalWebSocket()
  
  // Получаем последнее сообщение из сервиса
  const [lastMessage, setLastMessage] = useState<any>(null)
  
  useEffect(() => {
    if (service) {
      const handleMessage = (message: any) => {
        setLastMessage(message)
      }
      
      service.on('message', handleMessage)
      
      return () => {
        service.off('message', handleMessage)
      }
    }
  }, [service])
  
  // ============================================
  // ОБРАБОТКА WEBSOCKET СОБЫТИЙ
  // ============================================
  
  const handleWebSocketMessage = useCallback((message: any) => {
    if (!message || typeof message !== 'object') return
    
    try {
      // Парсим сообщение если это строка
      const parsedMessage = typeof message === 'string' ? JSON.parse(message) : message
      
      if (parsedMessage.type === 'notification' && parsedMessage.data) {
        const eventData = parsedMessage.data as WebSocketNotificationEvent['data']
        
        switch (eventData.type) {
          case 'notification':
            // Новое уведомление
            if (eventData.id && eventData.title && eventData.message) {
              const newNotification: UserNotification = {
                id: eventData.id,
                type: eventData.notification_type || eventData.type || 'system',
                title: eventData.title,
                message: eventData.message,
                order_id: eventData.order_id,
                offer_id: eventData.offer_id,
                related_data: eventData.related_data,
                is_read: false,
                priority: eventData.priority || 'normal',
                category: eventData.category,
                created_at: eventData.created_at || new Date().toISOString()
              }
              
              addNotification(newNotification)
              
              // Показываем браузерное уведомление если разрешено
              showBrowserNotification(newNotification)
            }
            break
            
          case 'notifications_marked_read':
            // Уведомления отмечены как прочитанные
            if (eventData.notification_ids && Array.isArray(eventData.notification_ids)) {
              updateNotificationStatus(eventData.notification_ids, true)
            }
            break
            
          case 'all_notifications_marked_read':
            // Все уведомления отмечены как прочитанные
            const allNotificationIds = notifications.map(n => n.id)
            updateNotificationStatus(allNotificationIds, true)
            break
            
          case 'notification_deleted':
            // Уведомление удалено
            if (eventData.notification_id) {
              removeNotification(eventData.notification_id)
            }
            break

          case 'all_notifications_deleted':
            logger.log(`✅ Все уведомления удалены: ${eventData.deleted_count}`)
            // Очищаем все уведомления из стейта через reset
            useNotificationsStore.getState().reset()
            break

          case 'order_notifications_deleted':
            logger.log(`🗑️ Удалены уведомления по заказу ${eventData.order_id}: ${eventData.deleted_count}`)
            // Используем существующий метод deleteNotificationsByOrder
            deleteNotificationsByOrder(eventData.order_id).catch(error => 
              logger.error('Ошибка удаления уведомлений по заказу:', error)
            )
            break
            
          default:
            logger.log('🔔 Неизвестный тип уведомления:', eventData.type)
        }
      }
    } catch (error) {
      logger.error('❌ Ошибка обработки WebSocket сообщения:', error)
    }
  }, [notifications, addNotification, updateNotificationStatus, removeNotification])
  
  // Обработка входящих WebSocket сообщений
  useEffect(() => {
    if (lastMessage) {
      handleWebSocketMessage(lastMessage)
    }
  }, [lastMessage, handleWebSocketMessage])
  
  // ============================================
  // БРАУЗЕРНЫЕ УВЕДОМЛЕНИЯ
  // ============================================
  
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission()
        logger.log('🔔 Разрешение на уведомления:', permission)
        return permission === 'granted'
      } catch (error) {
        logger.error('❌ Ошибка запроса разрешения на уведомления:', error)
        return false
      }
    }
    return Notification.permission === 'granted'
  }, [])
  
  const showBrowserNotification = useCallback((notification: UserNotification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const browserNotification = new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico', // Можно заменить на специальную иконку
          tag: notification.id, // Предотвращает дублирование
          requireInteraction: notification.priority === 'urgent',
          silent: notification.priority === 'low'
        })
        
        // Автозакрытие через 5 секунд для обычных уведомлений
        if (notification.priority !== 'urgent') {
          setTimeout(() => {
            browserNotification.close()
          }, 5000)
        }
        
        // Обработка клика по уведомлению
        browserNotification.onclick = () => {
          window.focus()
          // Можно добавить навигацию к заказу
          if (notification.order_id) {
            window.location.href = `/orders/${notification.order_id}`
          }
          browserNotification.close()
        }
      } catch (error) {
        logger.error('❌ Ошибка показа браузерного уведомления:', error)
      }
    }
  }, [])
  
  // ============================================
  // ИНИЦИАЛИЗАЦИЯ
  // ============================================
  
  useEffect(() => {
    // Запрашиваем разрешение на уведомления при первой загрузке
    requestNotificationPermission()
  }, [requestNotificationPermission])
  
  // ============================================
  // ВОЗВРАЩАЕМЫЕ МЕТОДЫ И СОСТОЯНИЕ
  // ============================================
  
  return {
    // Состояние
    notifications,
    unreadCount,
    totalCount,
    isLoading,
    error,
    isWebSocketConnected: isConnected,
    
    // Методы управления
    loadNotifications,
    refreshStats,
    markAsRead,
    markAllAsRead,
    deleteNotification: deleteNotificationById,
    deleteAllNotifications,
    deleteNotificationsByOrder,
    clearError,
    
    // Браузерные уведомления
    requestNotificationPermission,
    canShowBrowserNotifications: 'Notification' in window && Notification.permission === 'granted'
  }
}

/**
 * Хук для автоматической инициализации уведомлений
 * Использовать в App.tsx или главном компоненте
 */
export const useNotificationsInit = () => {
  const { loadNotifications, refreshStats } = useNotificationsStore()
  const { status, token } = useAuthStore()
  
  useEffect(() => {
    // Ждем успешной аутентификации и наличия токена
    if (status !== 'authenticated' || !token) {
      return
    }

    // Загружаем уведомления после авторизации
    loadNotifications({ limit: 10 })
    refreshStats()

    // Периодическое обновление статистики
    const interval = setInterval(() => {
      refreshStats()
    }, 60000) // Каждую минуту

    return () => clearInterval(interval)
  }, [status, token, loadNotifications, refreshStats])
}

/**
 * Хук для получения уведомлений конкретного заказа
 */
export const useOrderNotifications = (orderId: string) => {
  const notifications = useNotificationsStore(state => 
    state.notifications.filter(n => n.order_id === orderId)
  )
  
  return {
    notifications,
    unreadCount: notifications.filter(n => !n.is_read).length
  }
}

/**
 * Хук для получения статистики по типам уведомлений
 */
export const useNotificationStats = () => {
  const { notifications, unreadCount, totalCount } = useNotificationsStore()
  
  const statsByType = notifications.reduce((acc, notification) => {
    acc[notification.type] = (acc[notification.type] || 0) + 1
    return acc
  }, {} as Record<string, number>)
  
  const statsByPriority = notifications.reduce((acc, notification) => {
    acc[notification.priority] = (acc[notification.priority] || 0) + 1
    return acc
  }, {} as Record<string, number>)
  
  return {
    totalCount,
    unreadCount,
    readCount: totalCount - unreadCount,
    byType: statsByType,
    byPriority: statsByPriority
  }
}
